import { z } from 'zod';
import { apiEnvelope } from '../envelope';

// ─── Self-paced module content ──────────────────────────────────────────────
//
// The content of a self-paced CBT module as the server serves it to a member:
// an ordered list of steps, each made of cards, each card made of screens.
// Every member-facing string, the UI chrome included, is resolved server-side
// from the locale catalog, so the client renders what it is given and never
// knows which programme or substance the module is about.
//
// Response schemas stay non-strict: an older client strips a field the server
// adds later instead of rejecting the whole module. A card or screen of an
// unknown kind still fails the parse, because the client cannot render it.

export const moduleReadingCardSchema = z.object({
  kind: z.literal('reading'),
  id: z.string().min(1),
  title: z.string().min(1).optional(),
  pages: z.array(z.string().min(1)).min(1).readonly(),
});

/**
 * A guided session: a recording played above its transcript. A session
 * without a recording is read as text alone; one without a transcript is
 * skipped by the client.
 */
export const moduleAudioCardSchema = z.object({
  kind: z.literal('audio'),
  id: z.string().min(1),
  title: z.string().min(1),
  transcript: z.array(z.string().min(1)).readonly(),
  /** HTTPS URL of the session recording. */
  mediaUrl: z.string().url().startsWith('https://').optional(),
});

export const moduleQuizOptionSchema = z.object({
  value: z.string().min(1),
  label: z.string().min(1),
});

/**
 * One quiz question. A single right value makes it single-answer; several make
 * it multi-answer, and the member must pick exactly that many.
 */
export const moduleQuizQuestionSchema = z.object({
  id: z.string().min(1),
  prompt: z.string().min(1),
  options: z.array(moduleQuizOptionSchema).min(2).readonly(),
  correct: z.array(z.string().min(1)).min(1).readonly(),
  /** Teaching note shown after the answer is checked, right or wrong. */
  note: z.string().min(1),
});

/** A quiz. Its pass mark is zero: the score never blocks progress. */
export const moduleQuizCardSchema = z.object({
  kind: z.literal('quiz'),
  id: z.string().min(1),
  questions: z.array(moduleQuizQuestionSchema).min(1).readonly(),
});

export const moduleExerciseIntroScreenSchema = z.object({
  kind: z.literal('intro'),
  id: z.string().min(1),
  body: z.string().min(1),
});

export const moduleFreeTextScreenSchema = z.object({
  kind: z.literal('free-text'),
  id: z.string().min(1),
  length: z.enum(['long', 'short']),
  prompt: z.string().min(1),
  supporting: z.string().min(1).optional(),
  placeholder: z.string().min(1).optional(),
});

export const moduleNumberScreenSchema = z.object({
  kind: z.literal('number'),
  id: z.string().min(1),
  prompt: z.string().min(1),
  supporting: z.string().min(1).optional(),
  min: z.number().int(),
  max: z.number().int(),
});

export const moduleMultiSelectScreenSchema = z.object({
  kind: z.literal('multi-select'),
  id: z.string().min(1),
  prompt: z.string().min(1),
  supporting: z.string().min(1).optional(),
  /** Option strings are also the stored values, so downstream matching sees them verbatim. */
  options: z.array(z.string().min(1)).min(1).readonly(),
  minPicks: z.number().int().min(0),
});

export const moduleExerciseScreenSchema = z.discriminatedUnion('kind', [
  moduleExerciseIntroScreenSchema,
  moduleFreeTextScreenSchema,
  moduleNumberScreenSchema,
  moduleMultiSelectScreenSchema,
]);

/** An exercise: input screens, ending in a summary of the answers given. */
export const moduleExerciseCardSchema = z.object({
  kind: z.literal('exercise'),
  id: z.string().min(1),
  screens: z.array(moduleExerciseScreenSchema).min(1).readonly(),
});

export const moduleCardSchema = z.discriminatedUnion('kind', [
  moduleReadingCardSchema,
  moduleAudioCardSchema,
  moduleQuizCardSchema,
  moduleExerciseCardSchema,
]);

export const moduleStepSchema = z.object({
  /** Stable step key; member progress is recorded against it. */
  id: z.string().min(1),
  title: z.string().min(1),
  minutes: z.number().int().min(0),
  cards: z.array(moduleCardSchema).readonly(),
});

const copyString = z.string().min(1);

/**
 * UI chrome copy. Templates use `{{name}}` placeholders the client fills.
 */
export const moduleChromeCopySchema = z.object({
  closeLabel: copyString,
  backLabel: copyString,
  progressLabel: copyString,
  minutesTemplate: copyString,
  stateDone: copyString,
  stateUpNext: copyString,
  stateNotStarted: copyString,
  stateUnavailable: copyString,
  continueLabel: copyString,
  sessionDoneTitle: copyString,
  markFinishedLabel: copyString,
  audioPlayLabel: copyString,
  audioPauseLabel: copyString,
  audioSkipBackLabel: copyString,
  audioSkipForwardLabel: copyString,
  audioSeekLabel: copyString,
  audioLoadErrorLabel: copyString,
  audioPositionTemplate: copyString,
  audioControlNameTemplate: copyString,
  checkLabel: copyString,
  nextLabel: copyString,
  completeLabel: copyString,
  finishForTodayLabel: copyString,
  pageTemplate: copyString,
  questionTemplate: copyString,
  chooseTemplate: copyString,
  feedbackCorrect: copyString,
  feedbackIncorrect: copyString,
  feedbackMissed: copyString,
  optionFeedbackNameTemplate: copyString,
  stepTitleTemplate: copyString,
  stepAccessibilityTemplate: copyString,
  scoreTitle: copyString,
  scoreTemplate: copyString,
  scoreBody: copyString,
  summaryTitle: copyString,
  completeTitle: copyString,
  completeBody: copyString,
  finishedForTodayTitle: copyString,
  finishedForTodayBody: copyString,
  freeTextPlaceholder: copyString,
});

/** The fields of the chrome copy, in schema order. */
export const MODULE_CHROME_COPY_FIELDS = moduleChromeCopySchema.keyof().options;

export const moduleContentSchema = z.object({
  /** Stable module key; member progress is recorded against it. */
  id: z.string().min(1),
  title: z.string().min(1),
  copy: moduleChromeCopySchema,
  steps: z.array(moduleStepSchema).min(1).readonly(),
});

export const memberModuleContentResponseSchema = apiEnvelope(moduleContentSchema);

export type ModuleReadingCard = z.infer<typeof moduleReadingCardSchema>;
export type ModuleAudioCard = z.infer<typeof moduleAudioCardSchema>;
export type ModuleQuizOption = z.infer<typeof moduleQuizOptionSchema>;
export type ModuleQuizQuestion = z.infer<typeof moduleQuizQuestionSchema>;
export type ModuleQuizCard = z.infer<typeof moduleQuizCardSchema>;
export type ModuleExerciseIntroScreen = z.infer<typeof moduleExerciseIntroScreenSchema>;
export type ModuleFreeTextScreen = z.infer<typeof moduleFreeTextScreenSchema>;
export type ModuleNumberScreen = z.infer<typeof moduleNumberScreenSchema>;
export type ModuleMultiSelectScreen = z.infer<typeof moduleMultiSelectScreenSchema>;
export type ModuleExerciseScreen = z.infer<typeof moduleExerciseScreenSchema>;
export type ModuleExerciseCard = z.infer<typeof moduleExerciseCardSchema>;
export type ModuleCard = z.infer<typeof moduleCardSchema>;
export type ModuleStep = z.infer<typeof moduleStepSchema>;
export type ModuleChromeCopy = z.infer<typeof moduleChromeCopySchema>;
export type ModuleContent = z.infer<typeof moduleContentSchema>;
